/**
 * Keyword grouping intelligence — shared frontend + edge utility.
 *
 * Two keywords belong to the same group iff their MEANINGFUL words are
 * identical (same words, in any order). Connector tokens like "in", "the",
 * "de", "à", etc. are stripped before comparing.
 *
 * IMPORTANT: "near" is NOT a connector. It changes intent ("locksmith Rivonia"
 * vs "locksmith near Rivonia") and must be preserved.
 *
 * Also keep semantic modifiers like best/cheap/affordable/emergency/24hr/24/7/
 * mobile/fast/top — they change the keyword meaning.
 */

// Connectors stripped before comparison. Lowercased, exact-token match only.
const IGNORED_CONNECTORS = new Set([
  "in", "the", "a", "an",
  "de", "à", "en", "au", "du",
  "for", "of", "and",
  // NOTE: "near" intentionally NOT included.
]);

/** Tokenize: lowercase, split on any non-letter/digit run, drop empties. */
function tokenize(input: string): string[] {
  if (!input) return [];
  // Normalize accents-preserving lowercase, then split on non-alphanumeric
  // (Unicode letters preserved via \p{L}).
  const lower = String(input).toLowerCase().trim();
  // Use Unicode-aware splitter; fall back for older runtimes.
  try {
    return lower.split(/[^\p{L}\p{N}/]+/u).filter(Boolean);
  } catch {
    return lower.split(/[^a-z0-9/]+/i).filter(Boolean);
  }
}

/**
 * Normalize a keyword to its group key.
 *   "locksmith in Rivonia"  → "locksmith rivonia"
 *   "Rivonia locksmith"     → "locksmith rivonia"
 *   "locksmith near Rivonia"→ "locksmith near rivonia"  (near kept)
 */
export function normalizeKeyword(keyword: string): string {
  const tokens = tokenize(keyword)
    .filter((t) => !IGNORED_CONNECTORS.has(t));
  tokens.sort();
  return tokens.join(" ");
}

export type StatusTier = "winning" | "target" | "opportunity" | "discovery";

const TIER_RANK: Record<StatusTier, number> = {
  winning: 4,
  target: 3,
  opportunity: 2,
  discovery: 1,
};

function bestTier(a: StatusTier, b: StatusTier): StatusTier {
  return TIER_RANK[a] >= TIER_RANK[b] ? a : b;
}

export interface KeywordRowLike {
  keyword: string;
  status?: string | null;
  gsc_position?: number | null;
  gsc_impressions?: number | null;
  gsc_clicks?: number | null;
}

export interface KeywordGroup {
  normalized_key: string;
  canonical_keyword: string;
  total_impressions: number;
  total_clicks: number;
  best_position: number | null;
  status: StatusTier;
  variants: string[];
}

/**
 * Group an array of keyword rows by their normalized key.
 * Picks canonical = the variant with the highest impressions
 * (ties broken by best position, then by shorter string).
 */
export function groupKeywords(rows: KeywordRowLike[]): KeywordGroup[] {
  const buckets = new Map<string, KeywordRowLike[]>();
  for (const r of rows) {
    const kw = String(r.keyword || "").trim();
    if (!kw) continue;
    const key = normalizeKeyword(kw);
    if (!key) continue;
    const arr = buckets.get(key) || [];
    arr.push(r);
    buckets.set(key, arr);
  }

  const out: KeywordGroup[] = [];
  for (const [key, members] of buckets.entries()) {
    let total_impressions = 0;
    let total_clicks = 0;
    let best_position: number | null = null;
    let status: StatusTier = "discovery";
    const variantSet = new Set<string>();

    for (const m of members) {
      const imp = Number(m.gsc_impressions ?? 0) || 0;
      const clk = Number(m.gsc_clicks ?? 0) || 0;
      total_impressions += imp;
      total_clicks += clk;
      const pos = m.gsc_position == null ? null : Number(m.gsc_position);
      if (pos != null && !Number.isNaN(pos)) {
        if (best_position == null || pos < best_position) best_position = pos;
      }
      const s = (m.status as StatusTier) || "discovery";
      if (s === "winning" || s === "target" || s === "opportunity" || s === "discovery") {
        status = bestTier(status, s);
      }
      variantSet.add(String(m.keyword).trim());
    }

    // Canonical: highest impressions, then best position, then shortest string.
    const sorted = [...members].sort((a, b) => {
      const ia = Number(a.gsc_impressions ?? 0) || 0;
      const ib = Number(b.gsc_impressions ?? 0) || 0;
      if (ia !== ib) return ib - ia;
      const pa = a.gsc_position == null ? Number.POSITIVE_INFINITY : Number(a.gsc_position);
      const pb = b.gsc_position == null ? Number.POSITIVE_INFINITY : Number(b.gsc_position);
      if (pa !== pb) return pa - pb;
      return String(a.keyword).length - String(b.keyword).length;
    });
    const canonical = String(sorted[0].keyword).trim();

    out.push({
      normalized_key: key,
      canonical_keyword: canonical,
      total_impressions,
      total_clicks,
      best_position,
      status,
      variants: Array.from(variantSet),
    });
  }

  return out;
}
